// Auth store — Zustand. Access token lives in memory only (NEVER localStorage).
// Refresh is handled via __Host-rt cookie set by services/auth.
//
// Two primitive signals:
//   accessToken — null = no auth, string = authed.
//   probing     — true while the boot-time POST /v1/auth/refresh is in flight.
// Everything else (the "checking / needs_login / ready" trichotomy AuthGate
// uses) is derived in `selectBootStatus()` so it can never disagree with the
// live token. One source of truth.
//
// The session-hint cookie (eid_had_session) is set by services/auth alongside
// the refresh cookie. AuthGate reads it via document.cookie to decide whether
// the boot probe is worth attempting on first paint.

import type { AuthStep, Me } from '@eidollon/contracts/auth';
import { create } from 'zustand';

// Re-export so route files don't need to dual-import.
export type MeProfile = Me;
export type { AuthStep };

export type BootStatus = 'checking' | 'ready' | 'needs_login';

interface PendingEnroll {
  secret_base32: string;
  otpauth_uri: string;
  qr_svg: string;
}

interface AuthState {
  // Live auth proof.
  accessToken: string | null;
  me: Me | null;
  // True while the initial POST /v1/auth/refresh is in flight.
  probing: boolean;

  flowSession: string | null;
  pendingStep: AuthStep | null;
  // /mfa/enroll/start returns the secret + QR; held between start and confirm.
  pendingEnroll: PendingEnroll | null;
  // /mfa/enroll/confirm returns 10 recovery codes ONCE; held only long enough
  // for the user to save them.
  pendingRecoveryCodes: string[] | null;

  setAccess: (token: string, me: Me) => void;
  setFlow: (session: string | null, step: AuthStep | null) => void;
  setEnroll: (e: PendingEnroll | null) => void;
  setRecoveryCodes: (codes: string[] | null) => void;
  setProbing: (probing: boolean) => void;
  signOutLocal: () => void;
}

export const useAuth = create<AuthState>((set) => ({
  accessToken: null,
  me: null,
  probing: false,
  flowSession: null,
  pendingStep: null,
  pendingEnroll: null,
  pendingRecoveryCodes: null,

  setAccess: (token, me) => {
    set({
      accessToken: token,
      me,
      probing: false,
      flowSession: null,
      pendingStep: null,
      pendingEnroll: null,
      // intentionally KEEP pendingRecoveryCodes so the recovery-codes page
      // survives the access-token transition.
    });
  },

  setFlow: (session, step) => set({ flowSession: session, pendingStep: step }),
  setEnroll: (e) => set({ pendingEnroll: e }),
  setRecoveryCodes: (codes) => set({ pendingRecoveryCodes: codes }),
  setProbing: (probing) => set({ probing }),

  signOutLocal: () => {
    set({
      accessToken: null,
      me: null,
      probing: false,
      flowSession: null,
      pendingStep: null,
      pendingEnroll: null,
      pendingRecoveryCodes: null,
    });
  },
}));

/** Derive the AuthGate phase from the two primitive signals. */
export function selectBootStatus(s: AuthState): BootStatus {
  if (s.accessToken) return 'ready';
  if (s.probing) return 'checking';
  return 'needs_login';
}

/** Read the current access token outside of React. */
export function readAccessToken(): string | null {
  return useAuth.getState().accessToken;
}
